/**
 * BRE-8443: Next turns any non-RSC reply to a Server Action POST into the bare
 * E394 "unexpected response" and drops the status. Cloudflare, CloudFront and
 * the Lambda can each send that reply and none logs it, so this observes
 * `window.fetch` (Next calls it per request) and captures each reply as its own
 * event. Nothing links a reply to the error Next throws, so the E394 event gets
 * the reply's fields only when it is the sole candidate, else just the count.
 * Observes only: the caller gets the original Response, unread. Records no
 * body, query string, or cookie. BRE-9699: a header-overflow 413 to an action
 * or RSC request also raises the too-many-cookies toast.
 */

import posthog, { type CaptureResult } from "posthog-js";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { bufferOrCapture } from "@/lib/analytics/capture-buffer";
import { redactPublicLinkPath } from "@/lib/public-artifact-link";
import { deferHeaderOverflowToNextPage, surfaceHeaderOverflow } from "@/lib/header-overflow-recovery";

const UNEXPECTED_RESPONSE = "An unexpected response was received from the server";
/** A recorded reply this old belongs to some earlier failure, not this event. */
const MAX_AGE_MS = 60_000;
/**
 * BRE-9039: Lambda answers an oversized header block with the same 413 "must
 * be smaller than 6291456 bytes" it uses for an oversized payload, so only the
 * body size tells them apart. A body this small cannot reach 6 MB even
 * base64-encoded into the invoke payload, so its 413 was the headers.
 */
const HEADER_OVERFLOW_MAX_BODY_BYTES = 1_048_576;

/** Approximate size of a request body; null for a stream, whose size is unknown. */
export function requestBodyBytes(body: BodyInit | null | undefined): number | null {
  if (body == null) return 0;
  if (typeof body === "string") return new TextEncoder().encode(body).length;
  if (body instanceof URLSearchParams) return new TextEncoder().encode(body.toString()).length;
  if (body instanceof Blob) return body.size;
  if (body instanceof FormData) {
    let bytes = 0;
    for (const [, value] of body.entries()) {
      bytes += typeof value === "string" ? new TextEncoder().encode(value).length : value.size;
    }
    return bytes;
  }
  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) return body.byteLength;
  return null;
}

/** BRE-9699: a 413 to a request too small to be the payload is the headers. */
function isHeaderOverflow(status: number, bodyBytes: number | null): boolean {
  return status === 413 && bodyBytes !== null && bodyBytes < HEADER_OVERFLOW_MAX_BODY_BYTES;
}

type BadResponse = Record<string, string | number | boolean | null>;

let recent: Array<{ at: number; props: BadResponse }> = [];
let installed = false;

function pathOf(url: string): string | null {
  try {
    return redactPublicLinkPath(new URL(url, window.location.origin).pathname);
  } catch {
    return null;
  }
}

/** Mirrors Next's own E394 test in `server-action-reducer.js`. */
export function describeBadActionResponse(
  res: Response,
  actionId: string,
  requestBodyBytes: number | null = null,
): BadResponse | null {
  const h = res.headers;
  const contentType = h.get("content-type");
  if (contentType?.startsWith("text/x-component") || h.get("x-action-redirect")) return null;
  return {
    sa_status: res.status,
    sa_request_body_bytes: requestBodyBytes,
    // Null, not false, when the body size is unknown: unknown is not "no".
    sa_header_overflow: requestBodyBytes === null ? null : isHeaderOverflow(res.status, requestBodyBytes),
    sa_content_type: contentType,
    sa_redirected: res.redirected,
    sa_response_path: pathOf(res.url),
    // BRE-8058: a public link's path is its bearer token.
    sa_page_path: redactPublicLinkPath(window.location.pathname),
    sa_action_id: actionId,
    sa_content_length: h.get("content-length"),
    sa_server: h.get("server"),
    sa_cf_ray: h.get("cf-ray"),
    sa_amz_cf_id: h.get("x-amz-cf-id"),
    sa_amz_cf_pop: h.get("x-amz-cf-pop"),
    sa_x_cache: h.get("x-cache"),
  };
}

export function installServerActionResponseObserver(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const original = window.fetch;
  window.fetch = async function observedFetch(input, init) {
    const res = await original.call(this, input, init);
    try {
      const headers = new Headers(init?.headers);
      const actionId = init?.method === "POST" ? headers.get("next-action") : null;
      const bodyBytes = res.status === 413 || actionId ? requestBodyBytes(init?.body) : null;
      if (isHeaderOverflow(res.status, bodyBytes)) {
        if (actionId) surfaceHeaderOverflow("server-action");
        else if (headers.get("rsc") === "1") {
          if (headers.get("next-router-prefetch") === "1") surfaceHeaderOverflow("rsc-prefetch");
          // Next answers a failed navigation with a document load, which would unload the toast.
          else deferHeaderOverflowToNextPage();
        }
      }
      const props = actionId ? describeBadActionResponse(res, actionId, bodyBytes) : null;
      if (props) {
        const now = Date.now();
        recent = [...recent.filter((r) => now - r.at <= MAX_AGE_MS), { at: now, props }];
        bufferOrCapture(
          (name, p, options) => posthog.capture(name, p, options),
          ANALYTICS_EVENTS.SERVER_ACTION_BAD_RESPONSE,
          props,
        );
      }
    } catch {
      // Telemetry must never break the request.
    }
    return res;
  };
}

function isUnexpectedResponseEvent(cr: CaptureResult): boolean {
  const props = cr.properties as Record<string, unknown> | undefined;
  if (cr.event === "query_failed") return props?.classification === "unexpected_response";
  if (cr.event !== "$exception") return false;
  const list = props?.$exception_list;
  return (
    Array.isArray(list) &&
    list.some((item) => {
      const value = (item as { value?: unknown } | null)?.value;
      return typeof value === "string" && value.includes(UNEXPECTED_RESPONSE);
    })
  );
}

/** posthog-js `before_send` step. Fails open: returns the event untouched on any error. */
export function withServerActionResponse(cr: CaptureResult | null): CaptureResult | null {
  try {
    if (!cr || !isUnexpectedResponseEvent(cr)) return cr;
    const now = Date.now();
    const candidates = recent.filter((r) => now - r.at <= MAX_AGE_MS);
    if (candidates.length === 1) {
      cr.properties = { ...cr.properties, ...candidates[0].props };
    } else if (candidates.length > 1) {
      cr.properties = { ...cr.properties, sa_candidate_replies: candidates.length };
    }
  } catch {
    // Keep the event as it was.
  }
  return cr;
}

/** Test seam. */
export function resetServerActionResponseObserver(): void {
  recent = [];
}
